import React, { useState } from "react";
import {
    Container,
    Typography,
    Card,
    CardContent,
    Box,
    Grid,
    Button,
    Divider,
    Chip,
    Paper,
    ToggleButtonGroup,
    ToggleButton,
    Tooltip,
    LinearProgress
} from "@mui/material";
import {
    ViewList,
    ViewModule,
    KeyboardReturn,
    Assessment,
    EmojiEvents,
    TrendingUp,
    Refresh,
    Headphones as AudioIcon,
    Translate as TranslateIcon,
    Create as WriteIcon,
    SkipNext as SkipIcon,
    Mic as MicIcon,
    Shuffle as ShuffleIcon,
    TextFields as TextIcon,
    VolumeUp as VolumeUpIcon
} from "@mui/icons-material";

/**
 * CLEAN ReviewSummary Component
 * 
 * This is a PURE VIEW component that only displays data.
 * All calculations and state management happen in ReviewMode.
 * 
 * Props:
 * - summaryData: Complete pre-calculated data from ReviewMode
 * - onRestart: Function to restart review
 * - onReturn: Function to return to deck/course
 */
const ReviewSummary = ({ 
    summaryData,
    onRestart, 
    onReturn
}) => {
    const [viewMode, setViewMode] = useState("overview");

    // If no data provided, show loading state
    if (!summaryData) {
        return (
            <Container sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
                <Typography variant="h6">Loading review summary...</Typography>
            </Container>
        );
    }

    const { words, sentences, stats } = summaryData;

    /**
     * Get display info for variant types
     */
    const getVariantDisplayInfo = (variant) => {
        const iconStyle = { fontSize: '16px' };
        
        switch (variant.type) {
            case 'shadowing':
                return { 
                    icon: <MicIcon sx={iconStyle} />, 
                    label: "Shadowing",
                    color: "#9c27b0"
                };
            case 'audio-transcription':
                return { 
                    icon: (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <AudioIcon sx={{ fontSize: '14px' }} />
                            <WriteIcon sx={{ fontSize: '14px' }} />
                        </Box>
                    ),
                    label: "Audio → Text",
                    color: "#2196f3"
                };
            case 'audio-translation':
                return { 
                    icon: (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <AudioIcon sx={{ fontSize: '14px' }} />
                            <TranslateIcon sx={{ fontSize: '14px' }} />
                        </Box>
                    ),
                    label: "Audio → Translation",
                    color: "#3f51b5"
                };
            case 'sentence-reconstruction':
                return { 
                    icon: <ShuffleIcon sx={iconStyle} />, 
                    label: "Word Reordering",
                    color: "#ff9800"
                };
            case 'translation':
                return { 
                    icon: <TranslateIcon sx={iconStyle} />, 
                    label: "Translation",
                    color: "#4caf50"
                };
            default:
                return { 
                    icon: <TextIcon sx={iconStyle} />, 
                    label: "Text Input",
                    color: "#757575"
                };
        }
    };

    /**
     * Get status color and background
     */
    const getStatusStyle = (status) => {
        switch (status) {
            case 'correct':
                return { color: "#2e7d32", bg: "rgba(76, 175, 80, 0.1)", bgSolid: "#4caf50" };
            case 'incorrect':
                return { color: "#d32f2f", bg: "rgba(244, 67, 54, 0.1)", bgSolid: "#f44336" };
            case 'skipped':
                return { color: "#f57f17", bg: "rgba(255, 193, 7, 0.1)", bgSolid: "#ffc107" };
            case 'mixed':
                return { color: "#f57f17", bg: "rgba(255, 152, 0, 0.1)", bgSolid: "#ff9800" };
            default:
                return { color: "#757575", bg: "rgba(158, 158, 158, 0.1)", bgSolid: "#9e9e9e" };
        }
    };

    /**
     * Render variant status icon
     */
    const renderVariantIcon = (variant) => {
        const { icon, label, color } = getVariantDisplayInfo(variant);
        const statusStyle = getStatusStyle(variant.status);
        
        const iconElement = variant.status === "skipped" ? 
            <SkipIcon sx={{ fontSize: '16px' }} /> : 
            icon;

        return (
            <Tooltip 
                key={variant.id}
                title={`${label}: ${variant.status.charAt(0).toUpperCase() + variant.status.slice(1)}`}
            >
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: statusStyle.bg,
                        color: variant.status === "skipped" ? statusStyle.color : color,
                        borderRadius: '6px',
                        p: 0.75,
                        m: 0.25,
                        border: `1px solid ${variant.status === "skipped" ? statusStyle.color : color}`,
                        minWidth: '32px',
                        minHeight: '32px',
                        transition: 'all 0.2s',
                        '&:hover': {
                            transform: 'scale(1.1)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                        }
                    }}
                >
                    {iconElement}
                </Box>
            </Tooltip>
        );
    };

    /**
     * Get Chip color for status
     */
    const getChipColor = (status) => {
        switch (status) {
            case "correct": return "success";
            case "incorrect": return "error";
            case "skipped": return "warning";
            case "mixed": return "warning";
            default: return "default";
        }
    };

    /**
     * Create unified display items for list view
     */
    const createDisplayItems = () => {
        const items = [];
        
        // Add sentences
        sentences.forEach(sentence => {
            items.push({
                type: 'sentence',
                id: sentence.sentenceId,
                title: sentence.text,
                subtitle: sentence.translation,
                status: sentence.overallStatus,
                data: sentence,
                isSentence: true
            });
        });
        
        // Add words
        words.forEach(word => {
            items.push({
                type: 'word',
                id: word.id,
                title: word.question,
                subtitle: word.answer,
                status: word.status,
                data: word,
                isSentence: false
            });
        });
        
        // Sort by status (reviewed first, then by type)
        return items.sort((a, b) => {
            const statusOrder = { "correct": 0, "incorrect": 1, "mixed": 2, "skipped": 3, "unreviewed": 4 };
            const statusDiff = (statusOrder[a.status] || 4) - (statusOrder[b.status] || 4);
            if (statusDiff !== 0) return statusDiff;
            
            // Secondary sort: sentences first, then words
            if (a.isSentence && !b.isSentence) return -1;
            if (!a.isSentence && b.isSentence) return 1;
            return 0;
        });
    };

    const displayItems = createDisplayItems();

    return (
        <Container sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <Typography 
                variant="h4" 
                gutterBottom 
                align="center" 
                sx={{ 
                    fontWeight: 'bold', 
                    color: '#1a237e',
                    mb: 4
                }}
            >
                📊 Review Summary
            </Typography>

            {/* Main Statistics Card */}
            <Card sx={{ 
                mb: 4, 
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white'
            }}>
                <CardContent sx={{ p: 4 }}>
                    <Grid container spacing={4} alignItems="center">
                        {/* Total Progress */}
                        <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: "center" }}>
                                <Assessment fontSize="large" sx={{ mb: 2, fontSize: '3rem' }} />
                                <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 1 }}>
                                    Items Reviewed
                                </Typography>
                                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
                                    {stats.completedItems}/{stats.totalItems}
                                </Typography>
                                
                                {/* Progress bar */}
                                <LinearProgress 
                                    variant="determinate" 
                                    value={stats.totalItems > 0 ? (stats.completedItems / stats.totalItems) * 100 : 0}
                                    sx={{ 
                                        height: 8, 
                                        borderRadius: 4,
                                        backgroundColor: 'rgba(255,255,255,0.3)',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: '#4caf50'
                                        }
                                    }}
                                />
                                
                                {stats.unreviewedItems > 0 && (
                                    <Chip
                                        size="small"
                                        label={`${stats.unreviewedItems} not reviewed`}
                                        sx={{ 
                                            mt: 1,
                                            backgroundColor: 'rgba(255,255,255,0.2)',
                                            color: 'white',
                                            fontWeight: 'medium'
                                        }}
                                    />
                                )}
                            </Box>
                        </Grid>

                        {/* Accuracy */}
                        <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: "center" }}>
                                <EmojiEvents fontSize="large" sx={{ mb: 2, fontSize: '3rem' }} />
                                <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 1 }}>
                                    Accuracy Rate
                                </Typography>
                                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
                                    {stats.completedItems > 0 ? stats.accuracy : "-"}%
                                </Typography>
                                
                                {stats.completedItems > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip 
                                            size="small" 
                                            label={`${stats.correctItems} correct`}
                                            sx={{ backgroundColor: 'rgba(76, 175, 80, 0.9)', color: 'white' }}
                                        />
                                        <Chip 
                                            size="small" 
                                            label={`${stats.incorrectItems} incorrect`}
                                            sx={{ backgroundColor: 'rgba(244, 67, 54, 0.9)', color: 'white' }}
                                        />
                                        {stats.skippedItems > 0 && (
                                            <Chip 
                                                size="small" 
                                                label={`${stats.skippedItems} skipped`}
                                                sx={{ backgroundColor: 'rgba(255, 193, 7, 0.9)', color: 'white' }}
                                            />
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        {/* Breakdown */}
                        <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: "center" }}>
                                <TrendingUp fontSize="large" sx={{ mb: 2, fontSize: '3rem' }} />
                                <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 2 }}>
                                    Content Breakdown
                                </Typography>
                                
                                <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
                                    <Chip 
                                        size="medium" 
                                        label={`${stats.totalSentences} sentences (${stats.correctSentences} ✓, ${stats.incorrectSentences} ✗)`}
                                        sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                                    />
                                    <Chip 
                                        size="medium" 
                                        label={`${stats.totalWords} words (${stats.correctWords} ✓, ${stats.incorrectWords} ✗)`}
                                        sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* View Toggle */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, alignItems: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    Detailed Results
                </Typography>
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(e, newView) => newView && setViewMode(newView)}
                    size="small"
                    sx={{ borderRadius: '8px' }}
                >
                    <ToggleButton value="overview" sx={{ px: 2 }}>
                        <Tooltip title="Overview"><ViewModule /></Tooltip>
                    </ToggleButton>
                    <ToggleButton value="detailed" sx={{ px: 2 }}>
                        <Tooltip title="Detailed List"><ViewList /></Tooltip>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Results Display */}
            {viewMode === "overview" ? (
                /* Overview Mode - Cards with key info */
                <Grid container spacing={3}>
                    {displayItems.map((item) => (
                        <Grid item xs={12} sm={6} md={4} key={item.id}>
                            <Card sx={{
                                height: '100%',
                                boxShadow: '0 3px 12px rgba(0,0,0,0.1)',
                                borderRadius: '12px',
                                border: `2px solid ${getStatusStyle(item.status).bgSolid}`,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                                }
                            }}>
                                <CardContent sx={{ p: 2.5 }}>
                                    {/* Status chip */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Chip
                                            size="small"
                                            label={item.isSentence ? "Sentence" : "Word"}
                                            variant="outlined"
                                            sx={{ fontWeight: 'medium' }}
                                        />
                                        <Chip
                                            label={item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                            color={getChipColor(item.status)}
                                            size="small"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    </Box>

                                    {/* Content */}
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, lineHeight: 1.3 }}>
                                        {item.title.length > 50 ? `${item.title.substring(0, 50)}...` : item.title}
                                    </Typography>
                                    
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {item.subtitle.length > 60 ? `${item.subtitle.substring(0, 60)}...` : item.subtitle}
                                    </Typography>

                                    {/* Variants for sentences */}
                                    {item.isSentence && item.data.variants && (
                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, display: 'block' }}>
                                                Variants ({item.data.completedVariants}/{item.data.totalVariants}):
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {item.data.variants.map(variant => renderVariantIcon(variant))}
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Level change for words */}
                                    {!item.isSentence && item.data.newLevel !== item.data.oldLevel && (
                                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Level: {item.data.oldLevel} → {item.data.newLevel}
                                                {item.data.newLevel > item.data.oldLevel ? 
                                                    <span style={{ color: '#4caf50', marginLeft: 4 }}>📈</span> :
                                                    <span style={{ color: '#f44336', marginLeft: 4 }}>📉</span>
                                                }
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                /* Detailed Mode - Grouped by status */
                <Box>
                    {["correct", "incorrect", "mixed", "skipped", "unreviewed"].map(status => {
                        const items = displayItems.filter(item => item.status === status);
                        if (items.length === 0) return null;
                        
                        const statusStyle = getStatusStyle(status);
                        const statusLabels = {
                            correct: "✅ Completed Successfully",
                            incorrect: "❌ Need More Practice", 
                            mixed: "⚠️ Partially Completed",
                            skipped: "⏭️ Skipped",
                            unreviewed: "⏸️ Not Reviewed"
                        };
                        
                        return (
                            <Paper key={status} sx={{
                                p: 3, 
                                mb: 3, 
                                backgroundColor: statusStyle.bg,
                                borderRadius: '16px', 
                                boxShadow: '0 3px 12px rgba(0,0,0,0.08)',
                                border: `1px solid ${statusStyle.color}30`
                            }}>
                                <Typography variant="h6" sx={{ 
                                    mb: 3, 
                                    fontWeight: "bold", 
                                    color: statusStyle.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    {statusLabels[status]} ({items.length})
                                </Typography>
                                
                                <Grid container spacing={2}>
                                    {items.map((item) => (
                                        <Grid item xs={12} sm={6} lg={4} key={item.id}>
                                            <Card variant="outlined" sx={{
                                                backgroundColor: "white", 
                                                borderRadius: '12px',
                                                minHeight: '140px',
                                                transition: 'all 0.2s',
                                                border: `1px solid ${statusStyle.color}20`,
                                                '&:hover': { 
                                                    transform: 'translateY(-2px)', 
                                                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                                                    borderColor: statusStyle.color
                                                }
                                            }}>
                                                <CardContent sx={{ p: 2 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                        <Chip
                                                            size="small"
                                                            label={item.isSentence ? "Sentence" : "Word"}
                                                            variant="outlined"
                                                        />
                                                        {!item.isSentence && item.data.attempts > 0 && (
                                                            <Chip
                                                                size="small"
                                                                label={`${item.data.attempts} attempts`}
                                                                color="warning"
                                                            />
                                                        )}
                                                    </Box>
                                                    
                                                    <Tooltip title={item.title}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                            {item.title.length > 35 ? `${item.title.substring(0, 35)}...` : item.title}
                                                        </Typography>
                                                    </Tooltip>
                                                    
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                        {item.subtitle.length > 40 ? `${item.subtitle.substring(0, 40)}...` : item.subtitle}
                                                    </Typography>
                                                    
                                                    {/* Sentence variants */}
                                                    {item.isSentence && item.data.variants && (
                                                        <Box sx={{ mt: 1 }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, display: 'block' }}>
                                                                Variants:
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                                                {item.data.variants.map(variant => renderVariantIcon(variant))}
                                                            </Box>
                                                        </Box>
                                                    )}

                                                    {/* Level info for words */}
                                                    {!item.isSentence && (
                                                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Level: {item.data.oldLevel}
                                                                {item.data.newLevel !== item.data.oldLevel && (
                                                                    <> → {item.data.newLevel}</>
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                        );
                    })}
                </Box>
            )}

            {/* Action Buttons */}
            <Box sx={{ mt: 5, display: "flex", justifyContent: "center", gap: 3 }}>
                <Button 
                    variant="outlined" 
                    startIcon={<Refresh />} 
                    onClick={onRestart}
                    size="large"
                    sx={{ 
                        borderRadius: '12px', 
                        px: 4, 
                        py: 1.5,
                        borderWidth: 2,
                        '&:hover': { borderWidth: 2 }
                    }}
                >
                    New Review Session
                </Button>
                <Button 
                    variant="contained" 
                    startIcon={<KeyboardReturn />} 
                    onClick={onReturn}
                    size="large"
                    sx={{ 
                        borderRadius: '12px', 
                        px: 4, 
                        py: 1.5,
                        background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                        boxShadow: '0 3px 15px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                            background: 'linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)',
                            boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)'
                        }
                    }}
                >
                    Return to Deck
                </Button>
            </Box>
        </Container>
    );
};

export default ReviewSummary;